/**
 * Offline device registry calls (Phase 16A, design §8.5). One route, action in the body
 * (the dev HttpApi is close to its route limit).
 */
import { apiClient } from '../api-client';
import type {
  DeviceHeartbeatRequest,
  DeviceHeartbeatResponse,
  PendingDeviceDto,
  PendingDevicesResponse,
  RegisterDeviceRequest,
  RegisterDeviceResponse,
} from '../../dtos/offline/offline.dto';

const DEVICES = '/users/me/devices';
const DEVICE_IDS_KEY = 'pelora-offline-device-ids';

/**
 * Device id per enrolled identity, kept after sign-out so re-enrolling the same phone reuses its
 * registry entry instead of counting against the per-user device cap. Opaque ids only.
 */
export const DeviceIds = {
  get(identityId: string): string | undefined {
    try {
      const map = JSON.parse(localStorage.getItem(DEVICE_IDS_KEY) || '{}') as Record<string, string>;
      return typeof map[identityId] === 'string' ? map[identityId] : undefined;
    } catch {
      return undefined;
    }
  },
  set(identityId: string, deviceId: string): void {
    try {
      const map = JSON.parse(localStorage.getItem(DEVICE_IDS_KEY) || '{}') as Record<string, string>;
      localStorage.setItem(DEVICE_IDS_KEY, JSON.stringify({ ...map, [identityId]: deviceId }));
    } catch {
      // storage unavailable: the next enrollment registers a new device
    }
  },
};

export class DeviceService {
  /** Register (or re-register, with the device id this phone had before) for offline use. */
  static async register(platform: RegisterDeviceRequest['platform'], label: string, deviceId?: string): Promise<RegisterDeviceResponse> {
    const body: RegisterDeviceRequest = { action: 'register', platform, deviceClass: 'MOBILE', label, ...(deviceId ? { deviceId } : {}) };
    const response = await apiClient.post<RegisterDeviceResponse>(DEVICES, body);
    if (!response.data?.deviceId) throw new Error('Device registration failed');
    return response.data;
  }

  static async heartbeat(deviceId: string, pendingOps = 0, oldestPendingAt?: string, pendingVisitIds?: string[], pendingShiftIds?: string[]): Promise<DeviceHeartbeatResponse> {
    const body: DeviceHeartbeatRequest = { action: 'heartbeat', deviceId, pendingOps, oldestPendingAt, pendingVisitIds, pendingShiftIds };
    const response = await apiClient.post<DeviceHeartbeatResponse>(DEVICES, body);
    return response.data;
  }

  /** The user's enrolled devices with unsynced work (laptop hint, design §2.1). IDs only. */
  static async pending(): Promise<PendingDeviceDto[]> {
    const response = await apiClient.post<PendingDevicesResponse>(DEVICES, { action: 'pending' });
    return response.data?.devices || [];
  }
}

export default DeviceService;
